import { useCallback, useEffect, useRef, useState } from 'react';
import useBreakpoint from '@/hooks/useBreakpoint';
import { registrarEvento } from '@/services/googleAnalytics';

export function usePaginacaoScrollHorizontal(propsPaginacao: {
  totalItens: number | null | undefined;
  gap: number;
}) {
  const { totalItens, gap } = propsPaginacao;

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef<HTMLDivElement | HTMLButtonElement | null>(null);
  const estaScrollandoProgramaticamente = useRef(false);

  const [itensPorPagina, setItensPorPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [paginaAtual, setPaginaAtual] = useState(1);

  const { breakpoint } = useBreakpoint();

  const calcularPaginacao = useCallback(() => {
    const viewport = viewportRef.current;
    const item = itemRef.current;

    if (!viewport || !item) return;

    const larguraViewport = viewport.clientWidth;
    const larguraItem = item.clientWidth;
    const larguraComGap = larguraItem + gap;

    const quantidadeCabem =
      breakpoint === 'mobile'
        ? 1
        : Math.max(1, Math.floor((larguraViewport + gap) / larguraComGap));

    setItensPorPagina(quantidadeCabem);

    const paginasCalculadas = Math.max(
      1,
      Math.ceil((totalItens || 0) / quantidadeCabem),
    );

    setTotalPaginas(paginasCalculadas);
  }, [totalItens, gap, breakpoint]);

  useEffect(() => {
    calcularPaginacao();
    window.addEventListener('resize', calcularPaginacao);
    return () => window.removeEventListener('resize', calcularPaginacao);
  }, [calcularPaginacao]);

  function irParaPagina(indicePagina: number) {
    const viewport = scrollContainerRef.current;
    const item = itemRef.current;

    if (!viewport || !item) return;

    const larguraItem = item.clientWidth;
    const larguraComGap = larguraItem + gap;

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;

    const scrollXDesejado = (indicePagina - 1) * larguraComGap * itensPorPagina;

    const scrollXFinal = Math.min(scrollXDesejado, maxScrollLeft);

    estaScrollandoProgramaticamente.current = true;

    viewport.scrollTo({ left: scrollXFinal, behavior: 'smooth' });

    setPaginaAtual(Math.max(1, Math.min(totalPaginas, indicePagina)));

    setTimeout(() => {
      estaScrollandoProgramaticamente.current = false;
    }, 400);
  }

  function irParaProximaPagina() {
    irParaPagina(paginaAtual + 1);
  }

  function irParaPaginaAnterior() {
    irParaPagina(paginaAtual - 1);
  }

  // eslint-disable-next-line no-undef
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  function lidarComScroll(nomeLane?: string) {
    if (estaScrollandoProgramaticamente.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const viewport = scrollContainerRef.current;
      const item = itemRef.current;

      if (!viewport || !item) return;

      const larguraItem = item.clientWidth;
      const larguraComGap = larguraItem + gap;

      const paginaCalculada =
        Math.round(viewport.scrollLeft / (larguraComGap * itensPorPagina)) + 1;

      const paginaFinal = Math.max(1, Math.min(totalPaginas, paginaCalculada));

      setPaginaAtual(paginaFinal);
      registrarEvento('scrollLane', { lane: nomeLane });
    }, 100);
  }

  useEffect(() => {
    setPaginaAtual((paginaAtualAntiga) =>
      Math.min(paginaAtualAntiga, totalPaginas),
    );
  }, [itensPorPagina, totalPaginas]);

  return {
    viewportRef,
    scrollContainerRef,
    itemRef,
    paginaAtual,
    totalPaginas,
    itensPorPagina,
    irParaPagina,
    irParaProximaPagina,
    irParaPaginaAnterior,
    lidarComScroll,
  };
}
